import * as DialogPrimitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { type ComponentProps, createContext, useContext } from 'react'

import { transitions } from '@/lib/motion/presets'
import { cn } from '@/lib/utils'
import { useControllableOpen } from './use-controllable-open'

const DialogOpenContext = createContext(false)

function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  children,
  ...props
}: ComponentProps<typeof DialogPrimitive.Root>) {
  const [isOpen, setOpen] = useControllableOpen({
    open,
    defaultOpen,
    onOpenChange,
  })

  return (
    <DialogPrimitive.Root
      data-slot="dialog"
      open={isOpen}
      onOpenChange={setOpen}
      {...props}
    >
      <DialogOpenContext.Provider value={isOpen}>
        {children}
      </DialogOpenContext.Provider>
    </DialogPrimitive.Root>
  )
}

function DialogTrigger({
  ...props
}: ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  animated = true,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay> & { animated?: boolean }) {
  const isOpen = useContext(DialogOpenContext)

  return (
    <AnimatePresence>
      {isOpen && (
        <DialogPrimitive.Overlay
          data-slot="dialog-overlay"
          asChild
          forceMount
          {...props}
        >
          <motion.div
            initial={animated ? { opacity: 0 } : false}
            animate={
              animated
                ? { opacity: 1, transition: transitions.dropdown.open }
                : undefined
            }
            exit={
              animated
                ? { opacity: 0, transition: transitions.dropdown.close }
                : undefined
            }
            data-animation={animated ? 'default' : 'none'}
            className={cn(
              'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
              className
            )}
          />
        </DialogPrimitive.Overlay>
      )}
    </AnimatePresence>
  )
}

const dialogSizeClasses = {
  default: 'sm:max-w-lg',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-4xl',
  xl: 'sm:max-w-6xl',
} as const

function DialogContent({
  className,
  children,
  showCloseButton = true,
  hideDescription = false,
  size = 'default',
  expandFromPoint = false,
  animated = true,
  style,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  /** Set to true for dialogs that intentionally have no description to suppress accessibility warning */
  hideDescription?: boolean
  /** Controls the max-width of the dialog */
  size?: keyof typeof dialogSizeClasses
  /**
   * Scale the dialog in/out from its `transformOrigin` (set via `style`)
   * instead of the default centered fade+scale. Replaces the old
   * `.expand-dialog` CSS class; used for dialogs that grow from a trigger
   * button (see `transitions.expandDialog`).
   */
  expandFromPoint?: boolean
  /** Controls the dialog content and overlay transition. */
  animated?: boolean
}) {
  const isOpen = useContext(DialogOpenContext)

  const initialScale = expandFromPoint ? 0 : 0.96
  const exitScale = expandFromPoint ? 0 : 0.98

  const openTransition = expandFromPoint
    ? transitions.expandDialog.open
    : transitions.dropdown.open

  const closeTransition = expandFromPoint
    ? transitions.expandDialog.close
    : transitions.dropdown.close

  return (
    <AnimatePresence>
      {isOpen && (
        <DialogPortal forceMount>
          <DialogOverlay animated={animated} />
          <DialogPrimitive.Content
            data-slot="dialog-content"
            asChild
            forceMount
            aria-describedby={
              hideDescription ? undefined : props['aria-describedby']
            }
            {...props}
          >
            <motion.div
              initial={animated ? { opacity: 0, scale: initialScale } : false}
              animate={
                animated
                  ? { opacity: 1, scale: 1, transition: openTransition }
                  : undefined
              }
              exit={
                animated
                  ? {
                      opacity: 0,
                      scale: exitScale,
                      transition: closeTransition,
                    }
                  : undefined
              }
              data-animation={animated ? 'default' : 'none'}
              style={style}
              className={cn(
                'bg-background fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] rounded-lg border shadow-lg overflow-visible',
                dialogSizeClasses[size],
                className
              )}
            >
              {children}
              {showCloseButton && (
                <DialogPrimitive.Close
                  data-slot="dialog-close"
                  className="ring-offset-background focus-visible:ring-ring/50 data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-6 right-6 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-[3px] focus-visible:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                >
                  <XIcon />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              )}
            </motion.div>
          </DialogPrimitive.Content>
        </DialogPortal>
      )}
    </AnimatePresence>
  )
}

function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'flex flex-col gap-2 text-center sm:text-left p-6',
        className
      )}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row justify-end px-6 py-4 bg-secondary rounded-b-lg',
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}

// DialogBody component for content areas that need padding
function DialogBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div data-slot="dialog-body" className={cn('p-6', className)} {...props} />
  )
}

export { DialogBody }
